import React, { useMemo, useRef, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockIcon from "@mui/icons-material/Block";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { wellbeingContentApi } from "../api/wellbeingContent";
import type { WellbeingCarouselItem, WellbeingFact } from "../api/wellbeingContent";
import { useSessionQuery } from "../../../auth";
import { qk } from "../../../../lib/queryKeys";
import { PodcastEpisodesTab, MasterclassesTab, SendNotificationTab, UsageTab } from "../../settings/components";
import { masterclassesApi } from "../../settings/api/masterclassesApi";
import type { UpsertMasterclassPayload } from "../../settings/api/masterclassesApi";
import { clientManagementApi } from "../../settings/api/clientManagement";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORY_OPTIONS = ["Sleep", "Mind", "Hydration", "Movement", "Nutrition", "Recovery"];

const CATEGORY_COLORS: Record<string, "primary" | "secondary" | "info" | "success" | "warning" | "error"> = {
  Sleep: "primary",
  Mind: "secondary",
  Hydration: "info",
  Movement: "success",
  Nutrition: "warning",
  Recovery: "error",
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FactFormState = {
  id?: string;
  title: string;
  body: string;
  category: string;
  source: string;
  is_active: boolean;
  starts_on: string;
  ends_on: string;
  priority: number;
};

type CarouselFormState = {
  id?: string;
  title: string;
  body: string;
  media_type: "image" | "video";
  media_url: string;
  thumbnail_url: string;
  tag: string;
  action_label: string;
  action_url: string;
  is_active: boolean;
  members_only: boolean;
  starts_on: string;
  ends_on: string;
  priority: number;
};

const emptyFactForm: FactFormState = {
  title: "", body: "", category: "", source: "",
  is_active: true, starts_on: "", ends_on: "", priority: 0,
};

const emptyCarouselForm: CarouselFormState = {
  title: "", body: "", media_type: "image", media_url: "", thumbnail_url: "",
  tag: "", action_label: "", action_url: "",
  is_active: true, members_only: false, starts_on: "", ends_on: "", priority: 0,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const toDateInput = (value?: string | null) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const formatDisplayDate = (value?: string | null) => {
  const iso = toDateInput(value);
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const validateDateRange = (start: string, end: string) => {
  if (!start || !end) return null;
  if (start > end) return "Start date must be before end date.";
  return null;
};

const mapFactToForm = (fact: WellbeingFact): FactFormState => ({
  id: fact.id,
  title: fact.title ?? "",
  body: fact.body ?? "",
  category: fact.category ?? "",
  source: fact.source ?? "",
  is_active: fact.isActive ?? true,
  starts_on: toDateInput(fact.startsOn ?? null),
  ends_on: toDateInput(fact.endsOn ?? null),
  priority: fact.priority ?? 0,
});

const mapCarouselToForm = (item: WellbeingCarouselItem): CarouselFormState => ({
  id: item.id,
  title: item.title ?? "",
  body: item.body ?? "",
  media_type: item.mediaType === "video" ? "video" : "image",
  media_url: item.mediaUrl ?? item.imageUrl ?? "",
  thumbnail_url: item.imageUrl ?? "",
  tag: item.tag ?? "",
  action_label: item.actionLabel ?? "",
  action_url: item.actionUrl ?? "",
  is_active: item.isActive ?? true,
  members_only: item.membersOnly ?? false,
  starts_on: toDateInput(item.startsOn ?? null),
  ends_on: toDateInput(item.endsOn ?? null),
  priority: item.priority ?? 0,
});

// ---------------------------------------------------------------------------
// ExpandableBody
// ---------------------------------------------------------------------------

const ExpandableBody: React.FC<{ text: string; maxChars?: number }> = ({ text, maxChars = 160 }) => {
  const [expanded, setExpanded] = useState(false);
  const needsClamp = text.length > maxChars;
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
        {!expanded && needsClamp ? `${text.slice(0, maxChars).trimEnd()}…` : text}
      </Typography>
      {needsClamp && (
        <Button
          size="small"
          variant="text"
          sx={{ p: 0, minWidth: 0, fontSize: 12, mt: 0.25 }}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Less" : "More"}
        </Button>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// FactCard
// ---------------------------------------------------------------------------

const FactCard: React.FC<{
  fact: WellbeingFact;
  onEdit: () => void;
  onDeactivate: () => void;
  deactivating: boolean;
}> = ({ fact, onEdit, onDeactivate, deactivating }) => {
  const start = formatDisplayDate(fact.startsOn);
  const end = formatDisplayDate(fact.endsOn);
  const catColor = CATEGORY_COLORS[fact.category ?? ""] ?? "default";

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, "&:hover": { borderColor: "primary.main" }, transition: "border-color 0.15s" }}>
      <Box sx={{ p: 2 }}>
        {/* Header row */}
        <Stack direction="row" alignItems="flex-start" spacing={1} mb={0.75}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" mb={0.5}>
              {fact.category && (
                <Chip label={fact.category} size="small" color={catColor} variant="outlined" />
              )}
              <Chip
                label={fact.isActive ? "Active" : "Inactive"}
                size="small"
                color={fact.isActive ? "success" : "default"}
                variant={fact.isActive ? "filled" : "outlined"}
              />
            </Stack>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {fact.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Deactivate">
              <IconButton size="small" color="error" disabled={deactivating} onClick={onDeactivate}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Body */}
        <ExpandableBody text={fact.body ?? ""} />

        {/* Footer */}
        {(fact.priority != null || start || end || fact.source) && (
          <>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              {fact.priority != null && fact.priority > 0 && (
                <Typography variant="caption" color="text.secondary">Priority {fact.priority}</Typography>
              )}
              {start && (
                <Typography variant="caption" color="text.secondary">
                  {end ? `${start} → ${end}` : `From ${start}`}
                </Typography>
              )}
              {fact.source && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                  Source: {fact.source}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </Box>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// CarouselCard
// ---------------------------------------------------------------------------

const CarouselCard: React.FC<{
  item: WellbeingCarouselItem;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}> = ({ item, onEdit, onDelete, deleting }) => {
  const start = formatDisplayDate(item.startsOn);
  const end = formatDisplayDate(item.endsOn);
  const hasMedia = item.mediaUrl || item.imageUrl;
  const thumb = item.mediaType === "video" ? (item.imageUrl || item.mediaUrl) : (item.mediaUrl || item.imageUrl);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", display: "flex", flexDirection: "column", "&:hover": { borderColor: "primary.main" }, transition: "border-color 0.15s" }}>
      {/* Media */}
      {hasMedia ? (
        <Box sx={{ position: "relative" }}>
          <CardMedia
            component="img"
            image={thumb ?? ""}
            alt={item.title}
            sx={{ height: 160, objectFit: "cover" }}
          />
          {item.mediaType === "video" && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.3)" }}>
              <PlayCircleOutlineIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ height: 100, bgcolor: "action.hover", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ImageOutlinedIcon sx={{ fontSize: 36, color: "text.disabled" }} />
        </Box>
      )}

      <Box sx={{ p: 1.75, flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
        {/* Title + actions */}
        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
            {item.title}
          </Typography>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={onEdit}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" disabled={deleting} onClick={onDelete}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Chips */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {item.tag && <Chip label={item.tag} size="small" variant="outlined" />}
          <Chip label={item.isActive ? "Active" : "Inactive"} size="small" color={item.isActive ? "success" : "default"} variant={item.isActive ? "filled" : "outlined"} />
          {item.membersOnly && <Chip label="Members" size="small" color="primary" variant="outlined" />}
        </Stack>

        {/* Body */}
        {item.body && <ExpandableBody text={item.body} />}

        {/* Footer meta */}
        {(start || (item.priority != null && item.priority > 0)) && (
          <>
            <Divider />
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              {item.priority != null && item.priority > 0 && (
                <Typography variant="caption" color="text.secondary">Priority {item.priority}</Typography>
              )}
              {start && (
                <Typography variant="caption" color="text.secondary">
                  {end ? `${start} → ${end}` : `From ${start}`}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </Box>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Fact dialog
// ---------------------------------------------------------------------------

const FactDialog: React.FC<{
  open: boolean;
  form: FactFormState;
  onChange: (form: FactFormState) => void;
  onClose: () => void;
  onSave: () => void;
  isPending: boolean;
  isError: boolean;
}> = ({ open, form, onChange, onClose, onSave, isPending, isError }) => {
  const set = useCallback(
    <K extends keyof FactFormState>(key: K, value: FactFormState[K]) =>
      onChange({ ...form, [key]: value }),
    [form, onChange]
  );

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!form.title.trim()) e.title = "Required";
    if (!form.body.trim()) e.body = "Required";
    const d = validateDateRange(form.starts_on, form.ends_on);
    if (d) e.date = d;
    return e;
  }, [form]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{form.id ? "Edit fact" : "New fact"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isError && <Alert severity="error">Failed to save fact.</Alert>}

          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            error={Boolean(errors.title)}
            helperText={errors.title}
            fullWidth
            size="small"
            required
          />
          <TextField
            label="Body"
            value={form.body}
            onChange={(e) => set("body", e.target.value)}
            error={Boolean(errors.body)}
            helperText={errors.body || `${form.body.length} characters`}
            fullWidth
            size="small"
            multiline
            minRows={3}
          />

          <TextField
            select
            label="Category"
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">No category</MenuItem>
            {CATEGORY_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>

          <TextField
            label="Source"
            value={form.source}
            onChange={(e) => set("source", e.target.value)}
            fullWidth
            size="small"
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Starts on"
              type="date"
              value={form.starts_on}
              onChange={(e) => set("starts_on", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Ends on"
              type="date"
              value={form.ends_on}
              onChange={(e) => set("ends_on", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Stack>
          {errors.date && <Alert severity="warning" sx={{ py: 0.5 }}>{errors.date}</Alert>}

          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Priority"
              type="number"
              value={form.priority}
              onChange={(e) => set("priority", Number(e.target.value))}
              size="small"
              sx={{ width: 120 }}
            />
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} />}
              label="Active"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={Boolean(Object.keys(errors).length) || isPending}
          onClick={onSave}
        >
          {isPending ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Carousel dialog
// ---------------------------------------------------------------------------

const CarouselDialog: React.FC<{
  open: boolean;
  form: CarouselFormState;
  onChange: (form: CarouselFormState) => void;
  onClose: () => void;
  onSave: () => void;
  isPending: boolean;
  isError: boolean;
  uploading: boolean;
  uploadError: string | null;
  onUploadMedia: (file: File, field: "media_url" | "thumbnail_url") => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  thumbInputRef: React.RefObject<HTMLInputElement | null>;
}> = ({
  open, form, onChange, onClose, onSave, isPending, isError,
  uploading, uploadError, onUploadMedia, fileInputRef, thumbInputRef,
}) => {
  const set = useCallback(
    <K extends keyof CarouselFormState>(key: K, value: CarouselFormState[K]) =>
      onChange({ ...form, [key]: value }),
    [form, onChange]
  );

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!form.title.trim()) e.title = "Required";
    const d = validateDateRange(form.starts_on, form.ends_on);
    if (d) e.date = d;
    if ((form.action_label && !form.action_url) || (!form.action_label && form.action_url)) {
      e.action = "Both action label and URL must be set, or neither.";
    }
    if (form.media_type === "video" && !form.thumbnail_url.trim()) {
      e.thumbnail = "Thumbnail required for video.";
    }
    return e;
  }, [form]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{form.id ? "Edit carousel item" : "New carousel item"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isError && <Alert severity="error">Failed to save item.</Alert>}

          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            error={Boolean(errors.title)}
            helperText={errors.title}
            fullWidth
            size="small"
            required
          />
          <TextField
            label="Body"
            value={form.body}
            onChange={(e) => set("body", e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={3}
            helperText={`${form.body.length} characters`}
          />

          <Divider />

          {/* Media */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              Media · Image: 1600×900, ≤2MB · Video: MP4 1280×720–1920×1080, ≤50MB
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                select
                label="Media type"
                value={form.media_type}
                onChange={(e) => set("media_type", e.target.value as "image" | "video")}
                size="small"
                fullWidth
              >
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="video">Video</MenuItem>
              </TextField>

              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label={form.media_type === "video" ? "Video URL" : "Image URL"}
                  value={form.media_url}
                  onChange={(e) => set("media_url", e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ flexShrink: 0 }}
                >
                  {uploading ? <CircularProgress size={16} /> : "Upload"}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={form.media_type === "video" ? "video/*" : "image/*"}
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void onUploadMedia(file, "media_url");
                    if (e.target) e.target.value = "";
                  }}
                />
              </Stack>

              {form.media_type === "video" && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Thumbnail URL"
                    value={form.thumbnail_url}
                    onChange={(e) => set("thumbnail_url", e.target.value)}
                    size="small"
                    error={Boolean(errors.thumbnail)}
                    helperText={errors.thumbnail}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={uploading}
                    onClick={() => thumbInputRef.current?.click()}
                    sx={{ flexShrink: 0 }}
                  >
                    {uploading ? <CircularProgress size={16} /> : "Upload"}
                  </Button>
                  <input
                    ref={thumbInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void onUploadMedia(file, "thumbnail_url");
                      if (e.target) e.target.value = "";
                    }}
                  />
                </Stack>
              )}

              {uploadError && <Alert severity="error" sx={{ py: 0.5 }}>{uploadError}</Alert>}

              {/* Preview */}
              {form.media_type === "video" && form.media_url ? (
                <Box component="video" src={form.media_url} controls sx={{ width: "100%", maxHeight: 180, borderRadius: 1 }} />
              ) : form.media_url ? (
                <Box component="img" src={form.media_url} alt="Preview" sx={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 1 }} />
              ) : null}
            </Stack>
          </Box>

          <TextField label="Tag" value={form.tag} onChange={(e) => set("tag", e.target.value)} fullWidth size="small" />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Action label" value={form.action_label} onChange={(e) => set("action_label", e.target.value)} fullWidth size="small" />
            <TextField label="Action URL" value={form.action_url} onChange={(e) => set("action_url", e.target.value)} fullWidth size="small" />
          </Stack>
          {errors.action && <Alert severity="warning" sx={{ py: 0.5 }}>{errors.action}</Alert>}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Starts on" type="date" value={form.starts_on} onChange={(e) => set("starts_on", e.target.value)} InputLabelProps={{ shrink: true }} fullWidth size="small" />
            <TextField label="Ends on" type="date" value={form.ends_on} onChange={(e) => set("ends_on", e.target.value)} InputLabelProps={{ shrink: true }} fullWidth size="small" />
          </Stack>
          {errors.date && <Alert severity="warning" sx={{ py: 0.5 }}>{errors.date}</Alert>}

          <Stack direction="row" spacing={2} alignItems="center">
            <TextField label="Priority" type="number" value={form.priority} onChange={(e) => set("priority", Number(e.target.value))} size="small" sx={{ width: 120 }} />
            <FormControlLabel control={<Switch checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} />} label="Active" />
            <FormControlLabel control={<Switch checked={form.members_only} onChange={(e) => set("members_only", e.target.checked)} />} label="Members only" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={Boolean(Object.keys(errors).length) || isPending || uploading}
          onClick={onSave}
        >
          {isPending ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const WellbeingContent: React.FC = () => {
  const { data: session } = useSessionQuery();
  const isCentralAdmin = session?.user?.is_central_admin ?? false;
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("facts");
  const [factForm, setFactForm] = useState<FactFormState>(emptyFactForm);
  const [carouselForm, setCarouselForm] = useState<CarouselFormState>(emptyCarouselForm);
  const [factDialogOpen, setFactDialogOpen] = useState(false);
  const [carouselDialogOpen, setCarouselDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const thumbInputRef = useRef<HTMLInputElement | null>(null);

  // Filters
  const [factSearch, setFactSearch] = useState("");
  const [factCategory, setFactCategory] = useState("");
  const [carouselSearch, setCarouselSearch] = useState("");

  const factsQuery = useQuery({
    queryKey: qk.wellbeingFacts(),
    queryFn: () => wellbeingContentApi.listFacts(),
    enabled: isCentralAdmin,
  });

  const carouselQuery = useQuery({
    queryKey: qk.wellbeingCarousel(),
    queryFn: () => wellbeingContentApi.listCarousel(),
    enabled: isCentralAdmin,
  });

  const saveFactMutation = useMutation({
    mutationFn: (payload: FactFormState) =>
      payload.id
        ? wellbeingContentApi.updateFact(payload.id, {
            title: payload.title, body: payload.body,
            category: payload.category || null, source: payload.source || null,
            is_active: payload.is_active,
            starts_on: payload.starts_on || null, ends_on: payload.ends_on || null,
            priority: payload.priority,
          })
        : wellbeingContentApi.createFact({
            title: payload.title, body: payload.body,
            category: payload.category || null, source: payload.source || null,
            is_active: payload.is_active,
            starts_on: payload.starts_on || null, ends_on: payload.ends_on || null,
            priority: payload.priority,
          }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.wellbeingFacts() });
      setFactDialogOpen(false);
      setFactForm(emptyFactForm);
    },
  });

  const deactivateFactMutation = useMutation({
    mutationFn: (id: string) => wellbeingContentApi.deactivateFact(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.wellbeingFacts() }),
  });

  const saveCarouselMutation = useMutation({
    mutationFn: (payload: CarouselFormState) => {
      const base = {
        title: payload.title,
        body: payload.body || null,
        image_url: payload.media_type === "video" ? (payload.thumbnail_url || null) : (payload.media_url || null),
        media_url: payload.media_url || null,
        media_type: payload.media_type,
        tag: payload.tag || null,
        action_label: payload.action_label || null,
        action_url: payload.action_url || null,
        is_active: payload.is_active,
        members_only: payload.members_only,
        starts_on: payload.starts_on || null,
        ends_on: payload.ends_on || null,
        priority: payload.priority,
      };
      return payload.id
        ? wellbeingContentApi.updateCarousel(payload.id, base)
        : wellbeingContentApi.createCarousel(base);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.wellbeingCarousel() });
      setCarouselDialogOpen(false);
      setCarouselForm(emptyCarouselForm);
    },
  });

  const deleteCarouselMutation = useMutation({
    mutationFn: (id: string) => wellbeingContentApi.deleteCarousel(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.wellbeingCarousel() }),
  });

  // ── Masterclasses (central admin only) ──────────────────────────────────────
  const clientsQuery = useQuery({
    queryKey: qk.clients(),
    queryFn: () => clientManagementApi.listClients(),
    enabled: isCentralAdmin,
    staleTime: 2 * 60 * 1000,
  });

  const masterclassesQuery = useQuery({
    queryKey: qk.masterclasses(),
    queryFn: () => masterclassesApi.list(),
    enabled: isCentralAdmin,
  });

  const createMasterclassMutation = useMutation({
    mutationFn: (payload: UpsertMasterclassPayload) => masterclassesApi.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.masterclasses() }),
  });

  const updateMasterclassMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<UpsertMasterclassPayload> }) =>
      masterclassesApi.update(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.masterclasses() }),
  });

  const deleteMasterclassMutation = useMutation({
    mutationFn: (id: string) => masterclassesApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.masterclasses() }),
  });

  const onUploadMedia = async (file: File, field: "media_url" | "thumbnail_url") => {
    setUploading(true);
    setUploadError(null);
    try {
      const { upload_url, file_url } = await wellbeingContentApi.createCarouselUploadUrl({
        filename: file.name,
        content_type: file.type || "application/octet-stream",
      });
      await fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      setCarouselForm((prev) => ({ ...prev, [field]: file_url }));
    } catch {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Filtered data
  const filteredFacts = useMemo(() => {
    const facts = factsQuery.data ?? [];
    return facts.filter((f) => {
      const matchSearch = !factSearch || f.title.toLowerCase().includes(factSearch.toLowerCase()) || (f.body ?? "").toLowerCase().includes(factSearch.toLowerCase());
      const matchCat = !factCategory || f.category === factCategory;
      return matchSearch && matchCat;
    });
  }, [factsQuery.data, factSearch, factCategory]);

  const filteredCarousel = useMemo(() => {
    const items = carouselQuery.data ?? [];
    return !carouselSearch
      ? items
      : items.filter((i) => i.title.toLowerCase().includes(carouselSearch.toLowerCase()) || (i.tag ?? "").toLowerCase().includes(carouselSearch.toLowerCase()));
  }, [carouselQuery.data, carouselSearch]);

  if (!isCentralAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Central admin access required to manage wellbeing content.</Alert>
      </Box>
    );
  }

  const allFacts = factsQuery.data ?? [];
  const allCarousel = carouselQuery.data ?? [];

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
      {/* Header */}
      <Box mb={2}>
        <Typography variant="h6" fontWeight={700}>Wellbeing Content</Typography>
      </Box>

      {/* Tab bar */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 3,
          minHeight: 40,
          "& .MuiTab-root": { minHeight: 40, py: 0 },
        }}
      >
        <Tab value="facts" label={`Facts${allFacts.length ? ` (${allFacts.length})` : ""}`} sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }} />
        <Tab value="carousel" label={`Carousel${allCarousel.length ? ` (${allCarousel.length})` : ""}`} sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }} />
        <Tab value="podcast-episodes" label="Podcast Episodes" sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }} />
        {isCentralAdmin && (
          <Tab value="masterclasses" label="Masterclasses" sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }} />
        )}
        <Tab value="notifications" label="Notifications" sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }} />
        <Tab value="usage" label="App Usage" sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }} />
      </Tabs>

      {/* ── Facts tab ── */}
      {activeTab === "facts" && (
        <Stack spacing={2}>
          {/* Toolbar */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <TextField
              size="small"
              placeholder="Search facts…"
              value={factSearch}
              onChange={(e) => setFactSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment>,
                endAdornment: factSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFactSearch("")}><CloseIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <TextField
              select
              size="small"
              value={factCategory}
              onChange={(e) => setFactCategory(e.target.value)}
              sx={{ minWidth: 150 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><FilterListIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment>,
              }}
            >
              <MenuItem value="">All categories</MenuItem>
              {CATEGORY_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{ flexShrink: 0 }}
              onClick={() => { setFactForm(emptyFactForm); setFactDialogOpen(true); }}
            >
              New fact
            </Button>
          </Stack>

          {/* Count */}
          {(factSearch || factCategory) && (
            <Typography variant="caption" color="text.secondary">
              {filteredFacts.length} of {allFacts.length} facts
            </Typography>
          )}

          {factsQuery.isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading facts…</Typography>
            </Stack>
          ) : factsQuery.error ? (
            <Alert severity="error">Failed to load facts.</Alert>
          ) : filteredFacts.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
              <Typography variant="body2">{allFacts.length === 0 ? "No facts yet." : "No facts match your filters."}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }, gap: 2 }}>
              {filteredFacts.map((fact) => (
                <FactCard
                  key={fact.id}
                  fact={fact}
                  onEdit={() => { setFactForm(mapFactToForm(fact)); setFactDialogOpen(true); }}
                  onDeactivate={() => deactivateFactMutation.mutate(fact.id)}
                  deactivating={deactivateFactMutation.isPending}
                />
              ))}
            </Box>
          )}
        </Stack>
      )}

      {/* ── Carousel tab ── */}
      {activeTab === "carousel" && (
        <Stack spacing={2}>
          {/* Toolbar */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <TextField
              size="small"
              placeholder="Search carousel…"
              value={carouselSearch}
              onChange={(e) => setCarouselSearch(e.target.value)}
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment>,
                endAdornment: carouselSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setCarouselSearch("")}><CloseIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{ flexShrink: 0 }}
              onClick={() => { setCarouselForm(emptyCarouselForm); setCarouselDialogOpen(true); }}
            >
              New item
            </Button>
          </Stack>

          {carouselSearch && (
            <Typography variant="caption" color="text.secondary">
              {filteredCarousel.length} of {allCarousel.length} items
            </Typography>
          )}

          {deleteCarouselMutation.isError && <Alert severity="error">Failed to delete item.</Alert>}

          {carouselQuery.isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading carousel…</Typography>
            </Stack>
          ) : carouselQuery.error ? (
            <Alert severity="error">Failed to load carousel items.</Alert>
          ) : filteredCarousel.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
              <Typography variant="body2">{allCarousel.length === 0 ? "No carousel items yet." : "No items match your search."}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }, gap: 2 }}>
              {filteredCarousel.map((item) => (
                <CarouselCard
                  key={item.id}
                  item={item}
                  onEdit={() => { setCarouselForm(mapCarouselToForm(item)); setCarouselDialogOpen(true); }}
                  onDelete={() => {
                    if (window.confirm(`Delete "${item.title}"?`)) deleteCarouselMutation.mutate(item.id);
                  }}
                  deleting={deleteCarouselMutation.isPending}
                />
              ))}
            </Box>
          )}
        </Stack>
      )}

      {/* ── Podcast tab ── */}
      {activeTab === "podcast-episodes" && <PodcastEpisodesTab />}

      {activeTab === "masterclasses" && isCentralAdmin && (
        <MasterclassesTab
          createMasterclassMutation={createMasterclassMutation}
          updateMasterclassMutation={updateMasterclassMutation}
          deleteMasterclassMutation={deleteMasterclassMutation}
          masterclassesLoading={masterclassesQuery.isLoading}
          masterclassesList={masterclassesQuery.data?.data ?? []}
          clientsList={clientsQuery.data?.data ?? []}
        />
      )}

      {activeTab === "notifications" && (
        <SendNotificationTab
          isCentralAdmin={isCentralAdmin}
          clientsList={clientsQuery.data?.data ?? []}
        />
      )}

      {activeTab === "usage" && <UsageTab />}

      {/* Dialogs */}
      <FactDialog
        open={factDialogOpen}
        form={factForm}
        onChange={setFactForm}
        onClose={() => setFactDialogOpen(false)}
        onSave={() => saveFactMutation.mutate(factForm)}
        isPending={saveFactMutation.isPending}
        isError={saveFactMutation.isError}
      />

      <CarouselDialog
        open={carouselDialogOpen}
        form={carouselForm}
        onChange={setCarouselForm}
        onClose={() => setCarouselDialogOpen(false)}
        onSave={() => saveCarouselMutation.mutate(carouselForm)}
        isPending={saveCarouselMutation.isPending}
        isError={saveCarouselMutation.isError}
        uploading={uploading}
        uploadError={uploadError}
        onUploadMedia={onUploadMedia}
        fileInputRef={fileInputRef}
        thumbInputRef={thumbInputRef}
      />
    </Box>
  );
};
